import { useTranslation } from 'react-i18next';
import { useState, useEffect, useContext } from 'react';
import { gql, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Box } from '../../../1-primative';
import { TranslatableTextField } from '../../../2-component';
import {
  Dialog, DialogContent, DialogFooter, DialogTitle,
} from '../../../2-component/dialog/dialog';
import { Button } from '../../../2-component/button/button';
import { TranslatedString } from '../../../../interfaces';
import { useGlobalToast } from '../../../../providers/globalToastProvider';
import { UserContext } from '../../../../providers/userContextProvider';

const CLONE_MODEL_PORTFOLIO = gql`
mutation cloneModelPortfolio($input: CloneModelPortfolioInput!) {
  cloneModelPortfolio(input: $input) {
    modelPortfolio {
      id
      cloneOf
      translatedName {
        en
        fr
      }
      state
    }
  }
}
`;

interface CloneModelPortfolioDialogProps {
  open: boolean;
  onClose: () => void;
  modelId: string;
  modelName: TranslatedString;
}

export const CloneModelPortfolioDialog = ({
  open,
  onClose,
  modelId,
  modelName,
}: CloneModelPortfolioDialogProps) => {
  const { t } = useTranslation(['modelPortfolios']);
  const navigate = useNavigate();
  const { showToast } = useGlobalToast();
  const { activeOrganization } = useContext(UserContext);
  const [translatedName, setTranslatedName] = useState<TranslatedString>({
    en: modelName?.en ? `Copy of ${modelName.en}` : '',
    fr: modelName?.fr ? `Copie de ${modelName.fr}` : '',
  });

  useEffect(() => {
    if (modelName) {
      setTranslatedName({
        en: modelName.en ? `Copy of ${modelName.en}` : '',
        fr: modelName.fr ? `Copie de ${modelName.fr}` : '',
      });
    }
  }, [modelName]);

  const [cloneModelPortfolio, { loading }] = useMutation(CLONE_MODEL_PORTFOLIO, {
    refetchQueries: ['fetchModelPortfolios'],
  });

  const handleClone = async () => {
    try {
      const { data: cloneData, errors } = await cloneModelPortfolio({
        variables: {
          input: {
            modelPortfolioId: modelId,
            organizationId: activeOrganization?.id,
            translatedName,
          },
        },
      });

      if (errors) {
        showToast({
          message: errors[0].message,
          severity: 'error',
        });
      } else {
        showToast({
          message: t('modelPortfolios:success.cloneModelPortfolio'),
          severity: 'success',
        });
        navigate(`/modelPortfolios/${cloneData?.cloneModelPortfolio?.modelPortfolio?.id}`);
        onClose();
      }
    } catch (error: any) {
      showToast({
        message: error.message,
        severity: 'error',
      });
    }
  };

  const handleCancel = () => {
    setTranslatedName({ en: '', fr: '' });
    onClose();
  };

  return (
    <Dialog onClose={handleCancel} open={open} fullWidth>
      <DialogTitle>{t('modelPortfolioTable.actions.clone')}</DialogTitle>
      <DialogContent>
        <Box>
          <TranslatableTextField
            testId="clone-model-portfolio-name-field"
            label={t('modelPortfolioTable.name')}
            fullWidth
            value={translatedName}
            onChange={(e) => setTranslatedName({
              en: e.en ?? '',
              fr: e.fr ?? '',
            })}
          />
        </Box>
      </DialogContent>
      <DialogFooter>
        <Box display='flex' justifyContent='end' gap={2}>
          <Button
            variant='tonal'
            onClick={handleCancel}
            label={t('shared:cancel')}
            dataTestId="clone-model-portfolio-cancel-button"
          />
          <Button
            variant='filled'
            onClick={handleClone}
            label={t('shared:create')}
            loading={loading}
            disabled={!translatedName.en && !translatedName.fr}
            dataTestId="clone-model-portfolio-create-button"
          />
        </Box>
      </DialogFooter>
    </Dialog>
  );
};

export default CloneModelPortfolioDialog;
